import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

const LoomLogoScroller = () => {
  const scrollerRef = useRef(null);

  // University logos - all available logos
  const allCompanies = [
    { name: "USC", logo: "ulogo_usc.png" },
    { name: "University of Washington", logo: "ulogo_uw.png" },
    { name: "University of Wisconsin-Milwaukee", logo: "ulogo_uwm.svg" },
    { name: "Washington State University", logo: "ulogo_wsu.png" },
    { name: "UC Berkeley", logo: "ulogo_ucb.png" },
    { name: "UCLA", logo: "ulogo_ucla.png" },
    { name: "San Diego State University", logo: "ulogo_sdsu.png" },
    { name: "UC Irvine", logo: "ulogo_uci.png" },
    { name: "UNC", logo: "ulogo_unc.png" },
    { name: "Arizona State University", logo: "ulogo_asu.png" },
    { name: "University of Illinois", logo: "ulogo_uiuc.png" },
    { name: "Northwestern University", logo: "ulogo_northwestern.png" },
    { name: "University of Michigan", logo: "ulogo_umich.png" },
    { name: "NYU", logo: "ulogo_nyu.png" },
    { name: "University of Miami", logo: "ulogo_umiami.png" },
    { name: "University of Alabama", logo: "ulogo_ualabama.svg" },
    { name: "Ohio State University", logo: "ulogo_ohiostate.webp" },
    { name: "Brigham Young University", logo: "ulogo_byu.png" },
    { name: "Louisiana State University", logo: "ulogo_lsu.png" },
    { name: "University of Oregon", logo: "ulogo_uoregon.png" },
    { name: "University of New Mexico", logo: "ulogo_anm.png" },
    { name: "Georgetown University", logo: "ulogo_georgetown.png" },
    { name: "University of Pennsylvania", logo: "ulogo_upenn.png" },
    { name: "Stanford University", logo: "ulogo_stanford.png" },
    { name: "Cornell University", logo: "ulogo_cornell.png" },
    { name: "University of Missouri", logo: "ulogo_mizzou.png" },
    { name: "University of Oklahoma", logo: "ulogo_ou.png" },
    { name: "UT Austin", logo: "ulogo_uta.png" },
    { name: "Georgia Tech", logo: "ulogo_georgiatech.png" },
    { name: "MIT", logo: "ulogo_mit.png" },
    { name: "Michigan State", logo: "ulogo_michstate.svg" },
  ];

  // Shuffle and split logos into two groups
  const shuffledCompanies = [...allCompanies].sort(() => Math.random() - 0.5);
  const midPoint = Math.ceil(shuffledCompanies.length / 2);
  const firstLine = shuffledCompanies.slice(0, midPoint);
  const secondLine = shuffledCompanies.slice(midPoint);

  // Duplicate companies for seamless loop
  const duplicatedFirstLine = [...firstLine, ...firstLine];
  const duplicatedSecondLine = [...secondLine, ...secondLine];

  const firstScrollerRef = useRef(null);
  const secondScrollerRef = useRef(null);

  useEffect(() => {
    let animationId1, animationId2;

    const animateScroller1 = () => {
      if (!firstScrollerRef.current) return;

      let translateX = 0;
      const scrollSpeed = 0.5 * -1; // Scroll right (negative direction)
      const containerWidth = firstScrollerRef.current.parentElement.offsetWidth;
      const scrollerWidth = firstScrollerRef.current.offsetWidth;
      const singleSetWidth = scrollerWidth / 2; // Since we duplicated the content

      const animate = () => {
        translateX += scrollSpeed;

        // Reset position when we've scrolled through one complete set
        // For right scrolling, reset when we've moved by one set width
        if (translateX <= -singleSetWidth) {
          translateX = 0;
        }

        if (firstScrollerRef.current) {
          firstScrollerRef.current.style.transform = `translateX(${translateX}px)`;
        }
        animationId1 = requestAnimationFrame(animate);
      };

      animate();
    };

    const animateScroller2 = () => {
      if (!secondScrollerRef.current) return;

      const scrollSpeed = 0.5 * 1; // Scroll left (positive direction)
      const containerWidth =
        secondScrollerRef.current.parentElement.offsetWidth;
      const scrollerWidth = secondScrollerRef.current.offsetWidth;
      const singleSetWidth = scrollerWidth / 2; // Since we duplicated the content
      let translateX = -singleSetWidth; // Start from negative position to show logos immediately

      const animate = () => {
        translateX += scrollSpeed;

        // Reset position when we've scrolled through one complete set
        // For left scrolling, reset when we've moved by one set width
        if (translateX >= 0) {
          translateX = -singleSetWidth;
        }

        if (secondScrollerRef.current) {
          secondScrollerRef.current.style.transform = `translateX(${translateX}px)`;
        }
        animationId2 = requestAnimationFrame(animate);
      };

      animate();
    };

    // Start animations with a small delay to ensure refs are ready
    const timer1 = setTimeout(() => {
      animateScroller1();
    }, 100);

    const timer2 = setTimeout(() => {
      animateScroller2();
    }, 200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (animationId1) cancelAnimationFrame(animationId1);
      if (animationId2) cancelAnimationFrame(animationId2);
    };
  }, []);

  const renderScrollingLine = (companies, scrollerRef, isSmaller = false) => (
    <Box
      ref={scrollerRef}
      sx={{
        display: "flex",
        gap: { xs: "20px", sm: "24px", md: "48px" },
        alignItems: "center",
        whiteSpace: "nowrap",
        padding: { xs: "0 10px", md: "0 20px" },
        width: "max-content",
        willChange: "transform",
      }}
    >
      {companies.map((company, index) => (
        <Box
          key={`${company.name}-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: "fit-content",
            opacity: isSmaller ? 0.4 : 0.6,
            transition: "all 0.3s ease",
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: isSmaller
                ? { xs: "90px", sm: "105px", md: "150px" }
                : { xs: "120px", sm: "140px", md: "200px" },
              height: isSmaller
                ? { xs: "52px", sm: "60px", md: "90px" }
                : { xs: "70px", sm: "80px", md: "120px" },
              background: isSmaller
                ? "rgba(255, 255, 255, 0.04)"
                : "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              borderRadius: { xs: "12px", md: "16px" },
              border: isSmaller
                ? "1px solid rgba(255, 255, 255, 0.05)"
                : "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow:
                "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              padding: { xs: "20px", md: "30px" },
              overflow: "hidden",
              position: "relative",
              isolation: "isolate",
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 30%, transparent 70%, rgba(255, 255, 255, 0.02) 100%)",
                borderRadius: "inherit",
                pointerEvents: "none",
              },
              transition: "all 0.3s ease",
              "&:hover": {
                background: isSmaller
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(255, 255, 255, 0.12)",
              },
            }}
          >
            <img
              src={require(`../../images/${company.logo}`)}
              alt={`${company.name} logo`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: "brightness(0) invert(1)", // Makes logos white
              }}
              onError={(e) => {
                // Fallback to a simple text if image fails to load
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <Box
              sx={{
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 600,
                color: "#ffffff",
                textAlign: "center",
              }}
            >
              {company.name.split(" ")[0]}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          background: "transparent",
          padding: "40px 0 60px 0",
          position: "relative",
          zIndex: 10,
          overflow: "hidden", // Prevent horizontal scrolling
          width: "100%",
        }}
      >
        {/* First Scrolling Line */}
        <Box sx={{ width: "100%", overflow: "hidden" }}>
          {renderScrollingLine(duplicatedFirstLine, firstScrollerRef)}
        </Box>

        {/* Second Scrolling Line */}
        <Box
          sx={{
            marginTop: "20px",
            marginBottom: "60px",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {renderScrollingLine(duplicatedSecondLine, secondScrollerRef, true)}
        </Box>

        {/* Gradient overlays for seamless fade inwards */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: { xs: "200px", sm: "250px", md: "400px" },
            background:
              "linear-gradient(to right, #0c0c0c 0%, rgba(12, 12, 12, 0.8) 40%, rgba(12, 12, 12, 0.4) 60%, rgba(12, 12, 12, 0.2) 80%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: { xs: "200px", sm: "250px", md: "400px" },
            background:
              "linear-gradient(to left, #0c0c0c 0%, rgba(12, 12, 12, 0.8) 40%, rgba(12, 12, 12, 0.4) 60%, rgba(12, 12, 12, 0.2) 80%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      </Box>
    </>
  );
};

export default LoomLogoScroller;
